import { Proficiency, ProficiencyType } from "../models/dtos"
import { DataGridPro, GridToolbar } from '@mui/x-data-grid-pro';
import ProficiencyUpsertPopup from "../popups/ProficiencyUpsertPopup";
import { Securables, UpsertAction } from "../Enums";
import IconButtonEx from "./IconButtonEx";
import DeleteIcon from "@mui/icons-material/Delete";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ProficiencyClient, { ProficiencyErrors } from "../apiClients/ProficiencyClient";
import { useErrorDisplay } from "./ErrorDisplay";
import BusySpinner from "../components/BusySpinner";
import { useState } from "react";
import { Paper, Typography } from "@mui/material";
import { I18N } from "../I18N";
import OnlyForRole from "./OnlyForRole";
import MobileEntityCard from "./responsive/MobileEntityCard";
import ResponsiveCollection from "./responsive/ResponsiveCollection";
import useViewportMode from "../hooks/useViewportMode";

export interface ProficencyDataGridProps {
    data: Proficiency[];
    mode: UpsertAction;
    afterChange?: () => void;
    onAdd?: (id: string) => Promise<void>;
    height?: number | string;
}

const ProficiencyDataGrid = ({ data, mode, height, afterChange = () => { }, onAdd = async () => {} }: ProficencyDataGridProps) => {

    const [ErrorDisplay, setError] = useErrorDisplay();
    const [busy, setBusy] = useState<boolean>(false);
    const { mode: viewportMode } = useViewportMode();

    const handleDelete = async (id: string) => {
        try {
            setBusy(true);
            await new ProficiencyClient().Delete(id);
            afterChange();

        } catch (error) {
            setError(error, ProficiencyErrors.Delete);
        }
        finally {
            setBusy(false);
        }
    }

    const handleAdd = async (id: string) => {
        await onAdd(id);
    }

    const renderProficiencyCard = (proficiency: Proficiency) => (
        <MobileEntityCard
            ariaLabel={`Proficiency: ${proficiency.Name}`}
            title={proficiency.Name}
            subtitle={I18N.Translate(ProficiencyType[proficiency.Type] ?? String(proficiency.Type))}
            actions={
                <>
                    {mode === UpsertAction.View
                        ? <IconButtonEx
                            size="small"
                            hoverContent={I18N.Translate('Add Proficiency')}
                            visibleLabel={I18N.Translate('Add')}
                            aria-label={`${I18N.Translate('Add Proficiency')}: ${proficiency.Name}`}
                            onClick={() => handleAdd(proficiency.Id)}
                        >
                            <AddCircleOutlineIcon htmlColor="green" fontSize="small" />
                        </IconButtonEx>
                        : <OnlyForRole role={Securables.proficiency_modify}>
                            <ProficiencyUpsertPopup
                                showProficiencyGroupSelect={false}
                                mode={UpsertAction.Update}
                                hoverContent={I18N.Translate('Edit Proficiency')}
                                aria-label={`${I18N.Translate('Edit Proficiency')}: ${proficiency.Name}`}
                                onChange={async () => afterChange()}
                                proficiencyId={proficiency.Id}
                            />
                        </OnlyForRole>}
                    {mode === UpsertAction.Update && (
                        <OnlyForRole role={Securables.proficiency_delete}>
                            <IconButtonEx
                                size="small"
                                hoverContent={I18N.Translate('Delete Proficiency')}
                                aria-label={`${I18N.Translate('Delete Proficiency')}: ${proficiency.Name}`}
                                message={I18N.Translate('areyousure_DeleteProficiency')}
                                onClick={() => handleDelete(proficiency.Id)}
                            >
                                <DeleteIcon htmlColor="red" fontSize="small" />
                            </IconButtonEx>
                        </OnlyForRole>
                    )}
                </>
            }
        />
    );

    const desktopGrid = (
        <div style={{ height: height, width: '100%' }}>
            <DataGridPro
                density="compact"
                rows={data.map(x => (
                    {
                        id: x.Id,
                        Name: x.Name,
                        Type: x.Type,
                    }
                ))}
                columns={[
                    {
                        field: 'Action',
                        headerName: '',
                        sortable: false,
                        filterable: false,
                        disableColumnMenu: true,
                        width: 110,
                        renderCell: (params) => mode === UpsertAction.View
                            ? <IconButtonEx
                                size='small'
                                hoverContent={I18N.Translate('Add Proficiency')}
                                visibleLabel={I18N.Translate('Add')}
                                aria-label={`${I18N.Translate('Add Proficiency')}: ${params.row.Name}`}
                                onClick={() => handleAdd(params.row.id)}
                            >
                                <AddCircleOutlineIcon htmlColor='green' fontSize='small' />
                            </IconButtonEx>
                            : <OnlyForRole role={Securables.proficiency_modify}><ProficiencyUpsertPopup showProficiencyGroupSelect={false} mode={UpsertAction.Update} hoverContent={I18N.Translate("Edit Proficiency")} onChange={async () => afterChange()} proficiencyId={params.row.id} /></OnlyForRole>
                    },
                    {
                        field: 'Type',
                        headerName: I18N.Translate('Type'),
                        width: 150
                    },
                    {
                        field: 'Name',
                        headerName: I18N.Translate('Name'),
                        flex: 1
                    },
                    ...(mode === UpsertAction.Update ? [
                    {
                        field: 'Delete',
                        headerName: '',
                        sortable: false,
                        filterable: false,
                        disableColumnMenu: true,
                        width: 70,
                        renderCell: (params) => <OnlyForRole role={Securables.proficiency_delete}><IconButtonEx size='small' hoverContent={I18N.Translate('Delete Proficiency')} message={I18N.Translate('areyousure_DeleteProficiency')} onClick={() => handleDelete(params.row.id)}><DeleteIcon htmlColor='red' fontSize='small' /></IconButtonEx></OnlyForRole>
                    }] : [])
                ]}
                pageSize={15}
                components={{
                    Toolbar: GridToolbar,
                }}
            />
        </div>
    );

    return (
        <>
            <ErrorDisplay />
            <BusySpinner component={Paper} busy={busy}>
                <ResponsiveCollection
                    viewMode={viewportMode}
                    items={data}
                    getKey={proficiency => proficiency.Id}
                    renderCard={renderProficiencyCard}
                    desktop={desktopGrid}
                    ariaLabel={I18N.Translate('Proficiencies')}
                    emptyState={<Typography sx={{ p: 2 }} color="text.secondary">{I18N.Translate('No proficiencies found')}</Typography>}
                />
            </BusySpinner>
        </>
    );

}

export default ProficiencyDataGrid;
